// The SPA's one place for "render this in the reader's language".
//
// Three things used to decide that, each with its own hardcoded pair of
// languages: `Lang = "EN" | "TH"`, a `MESSAGES` dict keyed `en`/`th`, and
// `tLocal(item, base, lang)` which guessed at a `nameTh` field. Shipping a
// third language meant editing a union, a dict, every call-site, the database,
// and the API.
//
// Now there is one source: the locales the fixtures declare, generated into
// `LOCALES` and served by /api/reference. A language is data all the way down.

import { createContext, useContext, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { overwriteGetLocale } from "../../paraglide/runtime.js";
import { LOCALE, VOCABULARY } from "../../domain/vocabularies";
import { orpc } from "./orpc";
import type { ReactNode } from "react";
import {
  LOCALES,
  type Locale,
  type Localizer,
  type Names,
  type Reference,
  type Term,
} from "./localizer";

export * from "./localizer";

interface LocaleContextValue extends Localizer {
  setLocale(locale: Locale): void;
  /** The languages the API declares. The switcher renders from this. */
  available: Locale[];
  reference: Reference | undefined;
  /**
   * One vocabulary's terms, from the API if it has arrived and from the
   * compiled fixtures if it has not.
   *
   * The same fallback `label` has always had, for the same reason and one level
   * up. A `<select>` built from `reference?.ageGroups ?? []` renders *no
   * options* until the fetch lands — so the control is empty on first paint,
   * and permanently empty if the request fails. Labels degraded gracefully and
   * selects did not, which is the worse half to leave undefended.
   */
  terms(vocabulary: string): readonly Term[];
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

const STORAGE_KEY = "remy.locale";
const FALLBACK: Locale = "en";

function isLocale(v: string | null): v is Locale {
  return !!v && (LOCALES as readonly string[]).includes(v);
}

/**
 * The reader's language: their stored choice, else the browser's, else English.
 *
 * `navigator.languages` is consulted in order, so a Thai speaker with English
 * second lands on Thai without touching the switcher.
 */
function initialLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    // private mode, or storage disabled — fall through to the browser's list
  }
  for (const tag of navigator?.languages ?? []) {
    const base = tag.toLowerCase().split("-")[0]!;
    if (isLocale(base)) return base;
  }
  return FALLBACK;
}

/**
 * Paraglide reads the locale from here rather than keeping a cookie of its own.
 *
 * Two stores for "which language is the reader in" is the bug this whole day
 * was spent removing; `overwriteGetLocale` is the supported way to have one.
 * Set before first render so the very first message call is already correct.
 */
let currentLocale: Locale = initialLocale();
overwriteGetLocale(() => currentLocale);

/**
 * `<html lang>` on first paint, not only after the switcher is touched.
 *
 * index.html ships `lang="en"` and `setLocale` was the only thing that ever
 * wrote this attribute — so a Thai-preferring visitor got Thai content under
 * `lang="en"` for their entire first session, and forever if they never opened
 * the switcher. `initialLocale()` had already worked out the right answer one
 * line above; nothing was telling the document.
 *
 * It matters because both of the things that read this attribute read it on
 * load and not after a client-side change: a screen reader picks its voice and
 * pronunciation rules from it, and a crawler indexes the page as English. No
 * CSS here depends on it — the font stacks cover Thai and Japanese directly —
 * so this was invisible on screen, which is why it survived.
 *
 * Guarded like the localStorage read above: this module is imported by tests
 * that have no document.
 */
/**
 * Which way a language reads, from the model rather than from a list here.
 *
 * `direction` is declared on every LOCALE row, so adding Arabic is a change to
 * the Product Owner's model and not to this file. An unknown code reads
 * left-to-right, which is the safe default: a mis-declared language looks
 * ordinary rather than mirrored.
 */
export const directionOf = (locale: string): "ltr" | "rtl" =>
  (LOCALE.find((l) => l.code === locale) as { direction?: string } | undefined)?.direction === "rtl"
    ? "rtl"
    : "ltr";

/**
 * A language in its own name — `ไทย`, not "Thai".
 *
 * Here rather than in the sidebar that used to own it, because it is not the
 * switcher's private helper: anything that puts a language in front of a reader
 * wants this, and the alternative is `label("locales", code)`, which reads the
 * model's N×N `names` matrix — every language named in every other, 27 × 27,
 * which the model says is "not extended for new languages". Most of those cells
 * hold the English name, so that path shows an Arabic reader "Thai".
 *
 * `endonym` is one string per language, written once, and correct for the same
 * reason the picker uses it: somebody who cannot read the current interface has
 * to be able to find theirs, and nobody looking for Japanese scans for
 * `ญี่ปุ่น`.
 *
 * Falls back to the code so a language declared without one is visible rather
 * than blank; `tests/repo/messages.test.ts` refuses that case anyway.
 */
export const endonymOf = (locale: string): string =>
  (LOCALE.find((l) => l.code === locale) as { endonym?: string } | undefined)?.endonym ??
  locale.toUpperCase();

/**
 * `dir` goes on the document beside `lang`, and for a different reason.
 *
 * `lang` is read once at load by screen readers and crawlers. `dir` is read
 * continuously by the layout: it drives CSS logical properties, Tailwind's
 * `rtl:` variants and the browser's own bidirectional text algorithm, so it has
 * to change the moment the reader switches language rather than on next load.
 */
try {
  document.documentElement.lang = currentLocale;
  document.documentElement.dir = directionOf(currentLocale);
} catch {
  // no document — not a browser
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(currentLocale);

  // The vocabularies, through the same contract-typed query as everything else
  // — no bespoke fetch, no useEffect, no race guard. Reference data is
  // decoration rather than function: without it `label()` renders codes, which
  // is worse-looking but still navigable, so a failure must not blank the app.
  //
  // `staleTime: Infinity` rather than main.tsx's 30s default, for the same
  // reason useDevAccounts gives: these are the PO's fixtures, and a re-seed is
  // the only thing that changes them. At the default this refetched all 22
  // vocabularies on every remount and window refocus past 30 seconds — the
  // largest response the SPA asks for, for data that cannot have moved.
  // The locale is an input, so the endpoint sends this reader's language and
  // English rather than all twenty-seven — and it joins the query key, so
  // switching language fetches a different entry instead of invalidating this
  // one. docs/done/2026-09-09-17-reference-payload-per-locale.md.
  const { data: reference } = useQuery(
    orpc.reference.list.queryOptions({ input: { locale }, staleTime: Infinity }),
  );

  const value = useMemo<LocaleContextValue>(() => {
    // Seeded from the compiled vocabularies so the first paint is already
    // right. Without this a page renders `CHIANG_MAI` — a database code — for
    // however long the reference fetch takes, which under load is long enough
    // to see. The endpoint still wins once it arrives; this is the same data,
    // generated from the same fixtures, so the two cannot disagree.
    const index = new Map<string, Names>();
    // Descriptions come only from the endpoint: the compiled fallback carries
    // names so a label never renders blank, and a missing description renders
    // as nothing anyway, so there is nothing to degrade to.
    const described = new Map<string, Names>();
    for (const [vocabulary, terms] of Object.entries(VOCABULARY)) {
      for (const term of terms as readonly Term[]) {
        index.set(`${vocabulary}|${term.code}`, term.names);
      }
    }
    if (reference) {
      // Every vocabulary in the payload, whatever they are — the endpoint is
      // generated from the fixtures, so listing them here would fall behind.
      for (const [vocabulary, terms] of Object.entries(reference)) {
        for (const term of terms as Term[]) {
          index.set(`${vocabulary}|${term.code}`, term.names);
          if (term.descriptions) described.set(`${vocabulary}|${term.code}`, term.descriptions);
        }
      }
    }

    const name = (names: Names | undefined, fallback = "") =>
      names?.[locale] || names?.[FALLBACK] || fallback;

    return {
      locale,
      setLocale(next: Locale) {
        currentLocale = next;
        setLocaleState(next);
        document.documentElement.lang = next;
        document.documentElement.dir = directionOf(next);
        try {
          localStorage.setItem(STORAGE_KEY, next);
        } catch {
          // not worth failing a language switch over
        }
      },
      name,
      label: (vocabulary, code) => (code ? name(index.get(`${vocabulary}|${code}`), code) : ""),
      // No fallback to the code — see the note on `describe` in ./localizer.ts.
      describe: (vocabulary, code) => (code ? name(described.get(`${vocabulary}|${code}`)) : ""),
      // Falls back to the generated list so the switcher renders before the
      // vocabularies land, and still renders if they never do.
      // The fixtures define `locales` like any other vocabulary, so the
      // switcher renders from the same payload as every label.
      available: (reference?.locales.map((l) => l.code) ?? LOCALES).filter((l): l is Locale =>
        isLocale(l),
      ),
      // The endpoint wins when it arrives; the compiled list is generated from
      // the same fixtures, so the two cannot disagree about what exists.
      terms: (vocabulary: string) =>
        ((reference as Record<string, Term[]> | undefined)?.[vocabulary] ??
          (VOCABULARY as unknown as Record<string, readonly Term[]>)[vocabulary] ??
          []) as readonly Term[],
      reference,
    };
  }, [locale, reference]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (!value) throw new Error("useLocale must be used inside <LocaleProvider>");
  return value;
}

/** Just the resolving half, for the API mapping layer. */
export function useLocalizer(): Localizer {
  const { locale, name, label, describe } = useLocale();
  return useMemo(() => ({ locale, name, label, describe }), [locale, name, label, describe]);
}
